import React, { useState, useEffect } from "react";
import { useNavigation, useGetIdentity, useLogout } from "../../../contexts/AuthContext";
import { useTheme } from "../../../contexts/ThemeContext";
import logo from "../../../logo.svg";
import { Layout, Menu, Drawer, Avatar, Dropdown, Typography, Badge } from "antd";
import { SUGGESTIONS_CHANGED_EVENT, suggestionService } from "../../../services/deliveryService";
import {
  DashboardOutlined,
  UserOutlined,
  BankOutlined,
  ShareAltOutlined,
  CalendarOutlined,
  OrderedListOutlined,
  BookOutlined,
  SettingOutlined,
  ReadOutlined,
  CheckSquareOutlined,
  CustomerServiceOutlined,
  RiseOutlined,
  MessageOutlined,
  AuditOutlined,
  BellOutlined,
  LogoutOutlined,
  DownOutlined,
  MoreOutlined,
  ProjectOutlined,
  ToolOutlined,
  UserAddOutlined,
  ApartmentOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";

const { Sider } = Layout;
const { Text } = Typography;

// --- Components ---

// 1. Sidebar Header (Brand)
const SidebarHeader: React.FC<{ collapsed: boolean }> = ({ collapsed }) => {
  const { isDark } = useTheme();

  return (
    <div style={{
      padding: collapsed ? '16px 8px' : '16px',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      height: 64,
      borderBottom: `1px solid ${isDark ? '#333' : 'hsl(var(--border))'}`,
      transition: 'all 0.3s'
    }}>
      <div style={{
        width: 32,
        height: 32,
        background: '#FFC200',
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        <img src={logo} alt="DYEN" style={{ width: 20, height: 'auto' }} />
      </div>
      {!collapsed && (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, overflow: 'hidden' }}>
          <Text strong style={{ color: isDark ? '#fff' : '#000', fontSize: 14 }}>DYEN Admin</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>Enterprise</Text>
        </div>
      )}
    </div>
  );
};

// 2. Sidebar User Footer
const SidebarFooter: React.FC<{ collapsed: boolean }> = ({ collapsed }) => {
  const { data: user } = useGetIdentity();
  const { mutate: logout } = useLogout();
  const { push } = useNavigation();
  const { isDark } = useTheme();

  const handleLogout = () => {
    logout();
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => push('/admin/profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => push('/admin/settings'),
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Log out',
      danger: true,
      onClick: handleLogout,
    }
  ];

  const getUserDisplayName = () => {
    if (user?.name) return user.name;
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        return `${parsed.firstName || ""} ${parsed.lastName || ""}`.trim();
      } catch (e) { return "User"; }
    }
    return "User";
  };

  const getUserEmail = () => {
    if (user?.email) return user.email;
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        return parsed.email;
      } catch (e) { return ""; }
    }
    return "";
  }

  return (
    <div style={{
      borderTop: `1px solid ${isDark ? '#333' : 'hsl(var(--border))'}`,
      padding: collapsed ? '12px 8px' : '12px',
    }}>
      <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="topRight">
        <div
          className="sidebar-user-section sidebar-user-trigger"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '8px',
            borderRadius: 6,
            cursor: 'pointer',
            transition: 'background 0.2s',
          }}
        >
          <Avatar
            size={collapsed ? 32 : 36}
            style={{ backgroundColor: '#FFC200', color: '#000', flexShrink: 0 }}
            icon={<UserOutlined />}
          >
            {/* Fallback handled by icon if src fails or text */}
          </Avatar>

          {!collapsed && (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
              <Text strong ellipsis style={{ color: isDark ? '#fff' : '#000', fontSize: 13, lineHeight: 1.2 }}>
                {getUserDisplayName()}
              </Text>
              <Text type="secondary" ellipsis style={{ fontSize: 11, lineHeight: 1.2 }}>
                {getUserEmail()}
              </Text>
            </div>
          )}

          {!collapsed && <MoreOutlined style={{ color: 'hsl(var(--muted-foreground))', fontSize: 16 }} />}
        </div>
      </Dropdown>
    </div>
  );
};

interface SidebarProps {
  collapsed: boolean;
  setCollapsed: (val: boolean) => void;
  mobileDrawerOpen?: boolean;
  setMobileDrawerOpen?: (val: boolean) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  setCollapsed,
  mobileDrawerOpen = false,
  setMobileDrawerOpen,
}) => {
  const { push } = useNavigation();
  const { isDark } = useTheme();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const next = window.innerWidth < 768;
      setIsMobile(next);
      if (!next && mobileDrawerOpen) {
        setMobileDrawerOpen?.(false);
      }
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [mobileDrawerOpen, setMobileDrawerOpen]);

  // Pending library suggestions badge: on load, after a decision, and every 5 minutes.
  const [pendingSuggestions, setPendingSuggestions] = useState(0);
  useEffect(() => {
    let active = true;
    const refresh = () => {
      suggestionService
        .pendingCount()
        .then((count: number) => active && setPendingSuggestions(count))
        .catch(() => undefined);
    };
    refresh();
    const timer = window.setInterval(refresh, 5 * 60 * 1000);
    window.addEventListener(SUGGESTIONS_CHANGED_EVENT, refresh);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener(SUGGESTIONS_CHANGED_EVENT, refresh);
    };
  }, []);

  const getMenuItem = (
    label: React.ReactNode,
    key: React.Key,
    icon?: React.ReactNode,
    children?: MenuItem[],
    type?: "group"
  ): MenuItem => ({ key, icon, children, label, type } as MenuItem);

  type MenuItem = Required<MenuProps>["items"][number];

  // Grouped by job: who is joining, who is enrolled, what they learn and do, and running the school.
  // Keys are unchanged (routeMap below), so every page keeps its address.
  const items: MenuItem[] = [
    {
      type: 'group', label: 'Platform', children: [
        getMenuItem("Dashboard", "dashboard", <DashboardOutlined />),
        getMenuItem("Assistant", "assistant", <MessageOutlined />),
        getMenuItem("Assistant activity", "assistant-activity", <AuditOutlined />),
      ]
    },

    {
      type: 'group', label: 'People', children: [
        getMenuItem("Admissions", "admissions_group", <UserAddOutlined />, [
          getMenuItem("Applicants", "applicationsList"),
          getMenuItem("Prospective students", "ProspectiveStudents"),
          getMenuItem("Interviews", "interviewsList"),
          getMenuItem("Interview queue", "interviewQueue"),
          getMenuItem("Review", "review"),
          getMenuItem("Admission decisions", "Admissions"),
          getMenuItem("Application stats", "ApplicationStats"),
        ]),
        getMenuItem("Students", "Student Mgt", <UserOutlined />, [
          getMenuItem("Active", "Students"),
          getMenuItem("Deferred", "Deferred"),
          getMenuItem("Redlisted", "RedlistedStudents"),
          getMenuItem("Digital IDs", "digital-ids"),
          getMenuItem("Student stats", "StudentStats"),
        ]),
        getMenuItem("Programs & cohorts", "programs_group", <ApartmentOutlined />, [
          getMenuItem("Programs", "Programs"),
          getMenuItem("Cohorts", "cohorts"),
          getMenuItem("Intakes", "intakes"),
        ]),
        getMenuItem("Graduation", "graduation_group", <SafetyCertificateOutlined />, [
          getMenuItem("Certificates", "certificates"),
          getMenuItem("Internships", "internships"),
          getMenuItem("Graduation reservations", "graduation-reservations"),
        ]),
      ]
    },

    {
      type: 'group', label: 'Academics', children: [
        // This cohort's day-to-day work.
        getMenuItem("Teaching", "academics", <ReadOutlined />, [
          getMenuItem("Quizzes", "QuizDashboard"),
          getMenuItem("Tasks", "tasks"),
          getMenuItem("Grading", "grading"),
          getMenuItem("Class deliveries", "class-deliveries"),
          getMenuItem("Presentations", "presentations"),
        ]),
        getMenuItem("Attendance", "attendance_group", <CheckSquareOutlined />, [
          getMenuItem("Sessions", "attendance-sessions"),
          getMenuItem("Students", "attendance-students"),
          getMenuItem("Rules", "attendance-rules"),
        ]),
        // What every cohort reuses.
        getMenuItem("Curriculum", "curriculum_group", <BookOutlined />, [
          getMenuItem("Curriculum", "CurriculumManager"),
          getMenuItem("Courses", "courses"),
          getMenuItem("Practicals library", "practicals-library"),
          getMenuItem("Task & quiz library", "Schedules"),
          getMenuItem(
            <span>
              Suggested edits{" "}
              {pendingSuggestions ? <Badge count={pendingSuggestions} size="small" style={{ marginInlineStart: 4 }} /> : null}
            </span>,
            "library-suggestions"
          ),
        ]),
      ]
    },

    {
      type: 'group', label: 'Operations', children: [
        getMenuItem("Finance", "finance", <BankOutlined />, [
          getMenuItem("Overview", "financeOverview"),
          getMenuItem("Fees", "Fees"),
          getMenuItem("Transactions", "transactions"),
          getMenuItem("Student ledger", "student-ledger"),
          getMenuItem("Debt collection", "Debt Collection"),
          getMenuItem("Loans", "loan-manager"),
          getMenuItem("Audit", "Audit"),
          getMenuItem("Finance stats", "financialStatistics"),
        ]),
        getMenuItem("Gear", "gear_group", <ToolOutlined />, [
          getMenuItem("Items", "gear-items"),
          getMenuItem("Bundles", "gear-bundles"),
          getMenuItem("Bookings", "gear-bookings"),
          getMenuItem("Sign-offs", "gear-sign-offs"),
        ]),
        getMenuItem("Events", "events", <CalendarOutlined />),
        getMenuItem("Projects", "projects", <ProjectOutlined />),
        getMenuItem("Support tickets", "Tickets", <CustomerServiceOutlined />),
      ]
    },

    {
      type: 'group', label: 'Growth', children: [
        getMenuItem("Marketing", "growth", <RiseOutlined />, [
          getMenuItem("Overview", "MarketingDash"),
          getMenuItem("Leads", "LeadManager"),
          getMenuItem("Referrals", "referrals"),
        ]),
      ]
    },

    {
      type: 'group', label: 'Communication', children: [
        getMenuItem("Messaging", "communication", <MessageOutlined />, [
          getMenuItem("Email inbox", "EmailInbox"),
          getMenuItem("WhatsApp inbox", "WhatsAppInbox"),
          getMenuItem("Templates", "templates"),
          getMenuItem("Sender", "Sender"),
          getMenuItem("Sequences", "Sequences"),
          getMenuItem("Campaign contacts", "campaign-contacts"),
          getMenuItem("Campaigns", "campaigns"),
          getMenuItem("Notification feed", "notifications"),
        ]),
      ]
    },

    {
      type: 'group', label: 'System', children: [
        getMenuItem("Settings", "settings", <SettingOutlined />, [
          getMenuItem("Users", "Users"),
          getMenuItem("Notification settings", "NotificationSettings"),
          getMenuItem("AI settings", "ai-settings"),
        ]),
      ]
    }
  ];

  const handleClick: MenuProps["onClick"] = (e) => {
    const key = e.key;
    if (key === 'profile' || key === 'logout' || key === 'settings-user') return; // Handled by footer

    let route = "/admin/";
    // ... existing route logic (simplified mapping) ...
    // Since the original file had a massive switch statement, let's preserve the logic but cleaned up.
    // Ideally we'd use a map object, but for minimal regression risk I will reproduce the switch or a map.

    const routeMap: Record<string, string> = {
      dashboard: "dashboard",
      applicationsList: "applications",
      ApplicationStats: "ApplicationStats",
      Admissions: "Admissions",
      interviewsList: "interviews",
      review: "review",
      interviewsShow: "interviews/show",
      interviewQueue: "interview-queue",
      ProspectiveStudents: "prospective-students",
      Students: "Students",
      "digital-ids": "digital-ids",
      MarketingDash: "MarketingDash",
      StudentStats: "StudentStats",
      Deferred: "Deferred",
      financeOverview: "finance",
      fees: "fees",
      Fees: "Fees",
      transactions: "transactions",
      audit: "audit",
      Audit: "Audit",
      financialStatistics: "financial-statistics",
      FinancialReportsPage: "FinancialReportsPage",
      "Debt Collection": "debt-collection",
      "loan-manager": "loan-manager",
      referrals: "referralmanagement",
      standup: "standup",
      grading: "grading",
      tasks: "TaskManager",
      events: "events",
      "graduation-reservations": "graduation-reservations",
      projects: "projects",
      templates: "Templates",
      notifications: "Notifications",
      NotificationSettings: "NotificationSettings",
      "ai-settings": "ai-settings",
      Sender: "Sender",
      Sequences: "Sequences",
      "campaign-contacts": "campaign/contacts",
      campaigns: "campaign/campaigns",
      EmailInbox: "EmailInbox",
      Users: "Users",
      Programs: "Programs",
      cohorts: "cohorts",
      intakes: "cohorts/intakes",
      "student-ledger": "student-ledger",
      courses: "courses",
      certificates: "certificates",
      QuizDashboard: "QuizDashboard",
      Schedules: "Schedules",
      RedlistedStudents: "RedlistedStudents",
      internships: "internships",
      Tickets: "TicketManagement",
      CurriculumManager: "Curriculum",
      "practicals-library": "practicals-library",
      "library-suggestions": "library-suggestions",
      "class-deliveries": "class-deliveries",
      "gear-items": "gear/items",
      "gear-bundles": "gear/bundles",
      "gear-bookings": "gear/bookings",
      "gear-sign-offs": "gear/sign-offs",
      presentations: "presentations",
      WhatsAppInbox: "WhatsAppInbox",
      LeadManager: "LeadManager",
      assistant: "assistant",
      "assistant-activity": "assistant/activity",
      "attendance-sessions": "attendance/sessions",
      "attendance-students": "attendance/students",
      "attendance-rules": "attendance/rules",
    };

    if (routeMap[key]) {
      route += routeMap[key];
    } else {
      route += key;
    }

    push(route);
    if (isMobile) setMobileDrawerOpen?.(false);
  };

  const sidebarBg = isDark ? '#09090b' : '#FFFFFF'; // slightly darker for dark mode Shadcn feel
  const borderColor = isDark ? '#27272a' : '#E4E4E5';

  const SidebarContent = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: sidebarBg }}>
      <SidebarHeader collapsed={collapsed && !isMobile} />

      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }} className="custom-scrollbar">
        <Menu
          theme={isDark ? 'dark' : 'light'}
          mode="inline"
          defaultSelectedKeys={["dashboard"]}
          items={items}
          onClick={handleClick}
          inlineIndent={16}
          style={{
            background: sidebarBg,
            borderRight: 'none',
            fontSize: 13,
            fontWeight: 500,
          }}
          className="shadcn-menu"
        />
      </div>

      <SidebarFooter collapsed={collapsed && !isMobile} />
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        rootClassName="admin-mobile-drawer"
        placement="left"
        onClose={() => setMobileDrawerOpen?.(false)}
        open={mobileDrawerOpen}
        width="100vw"
        styles={{
          body: { padding: 0, background: sidebarBg },
          mask: { background: "rgba(0, 0, 0, 0.5)" },
        }}
        title="Navigation"
      >
        {SidebarContent}
      </Drawer>
    );
  }

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={setCollapsed}
      width={250}
      collapsedWidth={64}
      style={{
        background: sidebarBg,
        borderRight: `1px solid ${borderColor}`,
        height: '100vh',
        position: 'sticky',
        top: 0,
        left: 0,
        zIndex: 100, // Ensure it stays above content scroll
      }}
      trigger={null}
    >
      {SidebarContent}
    </Sider>
  );
};

export default Sidebar;
